import { useState } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  buttonVariants,
  Card,
  MissingDataHint,
  CardDescription,
  CardTitle,
  Meter,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  Tooltip,
  cn,
  formatCurrency,
  TrafficLight,
  formatNumber,
  formatPercent,
} from '@mgdlead/ui';
import { ChevronDown, ChevronRight, PlugZap } from 'lucide-react';

import { can } from '@mgdlead/shared';

import { useAdsConnections, type AnalyticsFunnelRow } from '../../lib/api';
import { formatRelative } from '../../lib/datetime';
import { useWorkspace } from '../workspace/WorkspaceContext';
import { AjustarOrcamentoDialog } from './AjustarOrcamento';

const TRACO = '—';

/** O que dizer quando não há visita para dividir — a frase muda com o MOTIVO. */
const SEM_VISITAS = {
  SEM_VINCULO_META:
    'Esta campanha ainda não foi ligada a uma campanha da Meta. Faça a ligação em Campanhas › ⋯ › Meta Ads para o número de visitas aparecer aqui.',
  SEM_DADO_DA_META:
    'A campanha está ligada, mas a Meta ainda não informou visitas na página neste período. Isso é diferente de zero visita.',
} as const;

const SEM_CAMPANHA_META =
  'Cliques e entradas deste bloco que não trazem o id de nenhuma campanha da Meta: link sem os parâmetros da Meta no anúncio ou tráfego orgânico. Ficam aqui para a soma fechar com o total do bloco.';

const SEM_CONJUNTO_META =
  'Cliques e entradas que vieram da campanha da Meta deste bloco, mas sem o id do conjunto (o anúncio não tem utm_term={{adset.id}}). Contam no bloco e no custo; só não aparecem na linha do conjunto.';

/**
 * "BQVPBK · 1 conjunto da Meta: Y1 criativo 1" — o bloco ligado a conjunto (campanha ABO,
 * 0041) diz que é conjunto e, sendo um só, qual.
 */
export function detalheDoBloco(f: AnalyticsFunnelRow): string {
  if (f.metaCampaigns.length === 0) return `${f.code} · sem campanha da Meta vinculada`;
  const campanhas = f.metaCampaigns.filter((m) => m.level !== 'ADSET').length;
  const conjuntos = f.metaCampaigns.filter((m) => m.level === 'ADSET');
  const partes: string[] = [];
  if (campanhas > 0) partes.push(`${campanhas} ${campanhas === 1 ? 'campanha' : 'campanhas'}`);
  if (conjuntos.length > 0) {
    partes.push(`${conjuntos.length} ${conjuntos.length === 1 ? 'conjunto' : 'conjuntos'}`);
  }
  const unico = conjuntos.length === 1 && campanhas === 0 ? conjuntos[0]?.name : null;
  return `${f.code} · ${partes.join(' + ')} da Meta${unico ? `: ${unico}` : ''}`;
}

const NAO_VINCULADA =
  'Campanha da Meta que mandou gente para este bloco sem estar vinculada a ele. O gasto dela não está no bloco, então as entradas dela ficam fora do custo por entrada e do CPL real até alguém vincular.';

type Meta = AnalyticsFunnelRow['meta'];

/** O que uma linha da árvore precisa — o bloco, cada campanha da Meta, e o "sem campanha". */
type LinhaDoFunil = {
  chave: string;
  nome: string;
  detalhe: string | null;
  href: string | null;
  visitas: number | null;
  motivoSemVisitas: string | null;
  /** Das entradas, quantas foram achadas na reconexão (entraram com o WhatsApp fora). */
  semHorario?: number;
  cliques: number;
  entradas: number;
  taxaDeClique: number | null;
  taxaDeEntrada: number | null;
  custos: AnalyticsFunnelRow['custos'] | null;
  evasao: AnalyticsFunnelRow['evasao'] | null;
  retencao24h: AnalyticsFunnelRow['retencao24h'] | null;
  /** Curva de Evasão (ADR-0019): por turma de calendário, com semáforo do servidor. */
  evasaoD1: AnalyticsFunnelRow['evasaoD1'] | null;
  evasaoD7: AnalyticsFunnelRow['evasaoD7'] | null;
  custoPorFicouD7Centavos: number | null;
  meta: Meta | null;
};

/**
 * O desempenho das campanhas: do anúncio ao grupo, com o CUSTO de cada passo.
 *
 * Pedido do dono em 06/10, com a tabela de um concorrente na mão: "ver as taxas mais
 * claramente, o CPL real, o gasto da campanha — não estou conseguindo achar isso". Os
 * números já existiam desde 05/10, mas moravam no rodapé da Gestão, debaixo de 23 grupos.
 *
 * A ordem das colunas segue a pergunta de quem paga o anúncio: quanto gastei → quanto a Meta
 * diz que custou → quanto custou DE VERDADE → onde o funil vazou. O "CPL Meta" e o "CPL real"
 * lado a lado são o argumento do produto: a distância entre os dois é o que só o MGDLead mede.
 */
export function CampaignPerformanceCard({
  rows,
  carregando,
  campaignHref,
  titulo = 'Desempenho das campanhas',
}: {
  rows: AnalyticsFunnelRow[] | undefined;
  carregando: boolean;
  /** Link da página da campanha, quando existir. */
  campaignHref?: (campaignId: string) => string;
  titulo?: string;
}) {
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());
  const { role } = useWorkspace();
  const podeAjustar = can(role, 'ads:budget');
  const [ajustando, setAjustando] = useState<{ nome: string; ids: string[] } | null>(null);
  if (carregando) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>{titulo}</CardTitle>
        <CardDescription>Carregando…</CardDescription>
      </Card>
    );
  }
  const linhas = (rows ?? []).filter(
    (r) => r.cliques > 0 || (r.visitas ?? 0) > 0 || (r.custos.gastoCentavos ?? 0) > 0,
  );
  if (linhas.length === 0) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>{titulo}</CardTitle>
        <CardDescription>
          Nenhuma campanha teve gasto, visita ou clique no período — os números aparecem quando o
          primeiro anúncio entrar no ar.
        </CardDescription>
      </Card>
    );
  }

  const alternar = (id: string) =>
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });

  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>{titulo}</CardTitle>
      <CardDescription>
        Do anúncio ao grupo. O <strong>CPL Meta</strong> é o que o Gerenciador mostra; o{' '}
        <strong>CPL real</strong> é o gasto dividido por quem entrou no grupo e ficou. Abra a
        campanha para ver cada campanha da Meta separada.
      </CardDescription>

      <Table caption="Desempenho por campanha" density="dense" className="mt-3" zebra>
        <TableHeader>
          <TableRow>
            <TableHead>Campanha</TableHead>
            <TableHead>Veiculação</TableHead>
            <TableHead>Investimento</TableHead>
            <TableHead>Orçamento hoje</TableHead>
            <TableHead>CPL Meta</TableHead>
            <TableHead>CPL entrada</TableHead>
            <TableHead>CPL real</TableHead>
            <TableHead>Visitas na LP</TableHead>
            <TableHead>Cliques no link</TableHead>
            <TableHead>Página → clique</TableHead>
            <TableHead>Taxa de entrada</TableHead>
            <TableHead>Entradas</TableHead>
            <TableHead>Evasão (entrou e saiu)</TableHead>
            <TableHead>Ficaram 24h</TableHead>
            <TableHead>Evasão 1º dia</TableHead>
            <TableHead>Evasão 7 dias</TableHead>
            <TableHead>Custo/ficou 7d</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {linhas.flatMap((f) => {
            const filhas =
              f.metaCampaigns.length +
              f.naoVinculadas.length +
              (f.semCampanhaMeta ? 1 : 0) +
              (f.semConjuntoMeta ? 1 : 0);
            // O ajuste de orçamento é por campanha; conjunto ligado a bloco não entra nele.
            const campanhasInteiras = f.metaCampaigns.filter((m) => m.level === 'CAMPAIGN');
            const aberto = abertos.has(f.campaignId);
            const bloco = (
              <LinhaFunil
                key={f.campaignId}
                linha={{
                  chave: f.campaignId,
                  nome: f.name,
                  href: campaignHref ? campaignHref(f.campaignId) : null,
                  detalhe: detalheDoBloco(f),
                  visitas: f.visitas,
                  motivoSemVisitas: SEM_VISITAS[f.motivoSemVisitas ?? 'SEM_DADO_DA_META'],
                  cliques: f.cliques,
                  entradas: f.entradas,
                  semHorario: f.entradasSemHorario,
                  taxaDeClique: f.taxaDeClique,
                  taxaDeEntrada: f.taxaDeEntrada,
                  custos: f.custos,
                  evasao: f.evasao,
                  retencao24h: f.retencao24h,
                  evasaoD1: f.evasaoD1,
                  evasaoD7: f.evasaoD7,
                  custoPorFicouD7Centavos: f.custoPorFicouD7Centavos,
                  meta: f.metaCampaigns.length === 0 ? null : f.meta,
                }}
                nivel={0}
                onAjustarOrcamento={
                  podeAjustar && campanhasInteiras.length > 0
                    ? () =>
                        setAjustando({
                          nome: f.name,
                          ids: campanhasInteiras.map((m) => m.externalId),
                        })
                    : undefined
                }
                expansivel={filhas > 0}
                aberto={aberto}
                onAlternar={() => alternar(f.campaignId)}
              />
            );
            if (!aberto) return [bloco];
            return [
              bloco,
              ...f.metaCampaigns.map((m) => (
                <LinhaFunil
                  key={`${f.campaignId}:${m.externalId}`}
                  linha={{
                    chave: m.externalId,
                    nome: m.name ?? m.externalId,
                    href: null,
                    detalhe:
                      m.name === null
                        ? 'nome ainda não importado da Meta'
                        : m.level === 'ADSET'
                          ? 'conjunto da Meta'
                          : null,
                    visitas: m.visitas,
                    motivoSemVisitas: SEM_VISITAS.SEM_DADO_DA_META,
                    cliques: m.cliques,
                    entradas: m.entradas,
                    semHorario: m.entradasSemHorario,
                    taxaDeClique: m.taxaDeClique,
                    taxaDeEntrada: m.taxaDeEntrada,
                    custos: m.custos,
                    evasao: m.evasao,
                    retencao24h: m.retencao24h,
                    evasaoD1: m.evasaoD1,
                    evasaoD7: m.evasaoD7,
                    custoPorFicouD7Centavos: m.custoPorFicouD7Centavos,
                    meta: m.meta,
                  }}
                  nivel={1}
                />
              )),
              ...f.naoVinculadas.map((n) => (
                <LinhaFunil
                  key={`${f.campaignId}:nv:${n.externalId}`}
                  linha={{
                    chave: `nv-${n.externalId}`,
                    nome: n.name ?? `Campanha ${n.externalId}`,
                    href: null,
                    detalhe: 'não vinculada · fora do custo — vincule pelo aviso do topo',
                    visitas: null,
                    motivoSemVisitas: NAO_VINCULADA,
                    cliques: n.cliques,
                    entradas: n.entradas,
                    taxaDeClique: null,
                    taxaDeEntrada: null,
                    custos: null,
                    evasao: null,
                    retencao24h: null,
                    evasaoD1: null,
                    evasaoD7: null,
                    custoPorFicouD7Centavos: null,
                    meta: null,
                  }}
                  nivel={1}
                />
              )),
              ...(f.semCampanhaMeta
                ? [
                    <LinhaFunil
                      key={`${f.campaignId}:sem-meta`}
                      linha={{
                        chave: 'sem-meta',
                        nome: 'Sem campanha da Meta identificada',
                        href: null,
                        detalhe: null,
                        visitas: null,
                        motivoSemVisitas: SEM_CAMPANHA_META,
                        cliques: f.semCampanhaMeta.cliques,
                        entradas: f.semCampanhaMeta.entradas,
                        taxaDeClique: null,
                        taxaDeEntrada: null,
                        custos: null,
                        evasao: null,
                        retencao24h: null,
                        evasaoD1: null,
                        evasaoD7: null,
                        custoPorFicouD7Centavos: null,
                        meta: null,
                      }}
                      nivel={1}
                      ajudaDoNome={SEM_CAMPANHA_META}
                    />,
                  ]
                : []),
              ...(f.semConjuntoMeta
                ? [
                    <LinhaFunil
                      key={`${f.campaignId}:sem-conjunto`}
                      linha={{
                        chave: 'sem-conjunto',
                        nome: 'Sem conjunto da Meta identificado',
                        href: null,
                        detalhe: 'conta no bloco e no custo',
                        visitas: null,
                        motivoSemVisitas: SEM_CONJUNTO_META,
                        cliques: f.semConjuntoMeta.cliques,
                        entradas: f.semConjuntoMeta.entradas,
                        taxaDeClique: null,
                        taxaDeEntrada: null,
                        custos: null,
                        evasao: null,
                        retencao24h: null,
                        evasaoD1: null,
                        evasaoD7: null,
                        custoPorFicouD7Centavos: null,
                        meta: null,
                      }}
                      nivel={1}
                      ajudaDoNome={SEM_CONJUNTO_META}
                    />,
                  ]
                : []),
            ];
          })}
        </TableBody>
      </Table>

      <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
        Investimento, visitas e CPL Meta vêm da Meta, pelas campanhas (ou conjuntos, na ABO)
        vinculadas a cada bloco. Cliques, entradas e evasão são nossos; entradas dependem do
        casamento clique↔entrada, então os CPLs por entrada são estimativa por cima.
      </p>
      <AjustarOrcamentoDialog
        aberto={ajustando !== null}
        onClose={() => setAjustando(null)}
        nomeDoBloco={ajustando?.nome ?? ''}
        externalIds={ajustando?.ids ?? []}
      />
    </Card>
  );
}

/**
 * Os totais do período, em cartões — o que se olha antes da tabela.
 *
 * O CPL real total é gasto TOTAL ÷ quem ficou no TOTAL, e não a média dos CPLs das linhas:
 * média de razões daria o mesmo peso a uma campanha de R$ 5 e a uma de R$ 500.
 */
export function CampaignTotals({
  rows,
  onVincularMeta,
  vincularMetaHref,
}: {
  rows: AnalyticsFunnelRow[] | undefined;
  /** G6: o botão "Vincular campanha" do custo sem gasto. Sem nenhum dos dois, só a frase. */
  onVincularMeta?: (() => void) | undefined;
  vincularMetaHref?: string | undefined;
}) {
  if (!rows) return null;
  const comGasto = rows.filter((r) => r.custos.gastoCentavos !== null);
  const gasto =
    comGasto.length === 0 ? null : comGasto.reduce((t, r) => t + (r.custos.gastoCentavos ?? 0), 0);
  const moeda = comGasto[0]?.custos.moeda ?? 'BRL';
  // Os custos só valem para quem tem gasto: entrada de campanha sem Meta não entra no divisor.
  // A base do custo vem do servidor SEM as campanhas da Meta não vinculadas (08/10): o gasto
  // delas não está no bloco, então as pessoas delas não podem dividir o custo.
  const entradasPagas = comGasto.reduce((t, r) => t + r.baseDoCusto.entradas, 0);
  const ficaramPagas = comGasto.reduce(
    (t, r) => t + r.baseDoCusto.entradas - r.baseDoCusto.sairamNoMesmoDia,
    0,
  );
  const leads = comGasto.reduce((t, r) => t + (r.meta.leads ?? 0), 0);
  const entradas = rows.reduce((t, r) => t + r.entradas, 0);
  // Pessoas que clicaram (10/10): a taxa de entrada é por PESSOA, régua do RealLead.
  const pessoas = rows.reduce((t, r) => t + r.unicos, 0);

  const div = (por: number) => (gasto === null || por <= 0 ? null : Math.round(gasto / por));
  const dinheiro = (c: number | null) => (c === null ? TRACO : formatCurrency(c / 100, moeda));

  const cartoes = [
    { rotulo: 'Investimento', valor: dinheiro(gasto), ajuda: 'Gasto da Meta no período.' },
    {
      rotulo: 'CPL Meta',
      valor: dinheiro(div(leads)),
      ajuda: 'Gasto ÷ leads que o pixel informou à Meta — o CPL do Gerenciador.',
    },
    {
      rotulo: 'CPL entrada',
      valor: dinheiro(div(entradasPagas)),
      ajuda:
        'Gasto ÷ quem entrou no grupo vindo das campanhas da Meta vinculadas (as não vinculadas ficam fora: o gasto delas não está aqui).',
    },
    {
      rotulo: 'CPL real',
      valor: dinheiro(div(ficaramPagas)),
      ajuda: 'Gasto ÷ quem entrou e NÃO saiu no mesmo dia. O custo da pessoa que ficou.',
      destaque: true,
    },
    {
      rotulo: 'Entradas',
      valor: formatNumber(entradas),
      ajuda: 'Entradas no grupo ligadas a um clique de campanha.',
    },
    {
      rotulo: 'Taxa de entrada',
      valor: pessoas > 0 ? formatPercent(Math.min(1, entradas / pessoas), 1) : TRACO,
      ajuda: 'De cada 100 pessoas que clicaram no link, quantas entraram no grupo.',
    },
  ];

  const comOrcamento = rows.filter((r) => r.meta.orcamentoDiarioCentavos !== null);
  const orcamento =
    comOrcamento.length === 0
      ? null
      : comOrcamento.reduce((t, r) => t + (r.meta.orcamentoDiarioCentavos ?? 0), 0);
  const gastoHoje = comOrcamento.reduce((t, r) => t + (r.meta.gastoHojeCentavos ?? 0), 0);

  /*
   * G6 (07/10): o "—" do custo tem causa conhecida quando NENHUM bloco está ligado à Meta — e
   * o conserto é um clique. Com ligação e sem gasto informado, o motivo é outro (a Meta ainda
   * não mandou), e aí não há botão: o tooltip de cada tile explica.
   */
  const semLigacaoComAMeta = gasto === null && rows.every((r) => r.metaCampaigns.length === 0);
  const botaoVincular = onVincularMeta ? (
    <Button size="sm" onClick={onVincularMeta}>
      Vincular campanha
    </Button>
  ) : vincularMetaHref ? (
    <Link to={vincularMetaHref} className={buttonVariants({ size: 'sm' })}>
      Vincular campanha
    </Link>
  ) : null;

  return (
    <div className="flex flex-col gap-3">
      {semLigacaoComAMeta ? (
        <MissingDataHint icon={PlugZap} action={botaoVincular}>
          Vincule uma campanha da Meta para ver quanto custa cada entrada.
        </MissingDataHint>
      ) : null}
      {orcamento !== null && orcamento > 0 ? (
        <Card padded={false} className="p-3 md:p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <span className="text-muted-foreground">Orçamento de hoje</span>
            <span className="nums">
              <strong className="text-foreground">{dinheiro(gastoHoje)}</strong>{' '}
              <span className="text-muted-foreground">de {dinheiro(orcamento)} ·</span>{' '}
              <strong className="text-foreground">{formatPercent(gastoHoje / orcamento, 0)}</strong>
            </span>
          </div>
          <div className="mt-2 h-2.5 rounded-pill bg-muted">
            <div
              className="h-full rounded-pill bg-primary"
              style={{ width: `${Math.min(100, (gastoHoje / orcamento) * 100)}%` }}
            />
          </div>
        </Card>
      ) : null}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {cartoes.map((c) => (
          <Card
            key={c.rotulo}
            padded={false}
            className={c.destaque ? 'border-primary p-3 md:p-4' : 'p-3 md:p-4'}
          >
            <Tooltip content={c.ajuda}>
              <span tabIndex={0} className="cursor-help text-xs text-muted-foreground">
                {c.rotulo}
              </span>
            </Tooltip>
            <span className="nums mt-1 block text-xl font-semibold text-foreground">{c.valor}</span>
          </Card>
        ))}
      </div>
    </div>
  );
}

/**
 * Orçamento de HOJE e quanto já foi: "R$ 9 de R$ 50" + barrinha. Independe do período da
 * tela — orçamento diário é sempre sobre o dia de hoje.
 */
function Orcamento({
  meta,
  moeda,
  onAjustar,
}: {
  meta: Meta | null;
  moeda: string | null;
  onAjustar?: (() => void) | undefined;
}) {
  const ajustar = onAjustar ? (
    <button
      type="button"
      onClick={onAjustar}
      className="mt-0.5 self-start text-xs font-medium text-primary-text underline-offset-2 hover:underline"
    >
      Ajustar
    </button>
  ) : null;
  if (!meta || meta.orcamentoDiarioCentavos === null) {
    return (
      <TableCell nowrap>
        <Tooltip content="Sem orçamento diário: a campanha usa orçamento vitalício, ou a Meta ainda não informou (a próxima leitura sai em até 10 min).">
          <span tabIndex={0} className="cursor-help text-muted-foreground">
            {TRACO}
          </span>
        </Tooltip>
        {ajustar ? <span className="ml-2">{ajustar}</span> : null}
      </TableCell>
    );
  }
  const m = moeda ?? 'BRL';
  const usado = meta.orcamentoUsado ?? 0;
  return (
    <TableCell nowrap>
      <Tooltip
        content={`Hoje já foram ${formatPercent(usado, 0)} do orçamento diário. Na campanha com orçamento por conjunto (ABO), é a soma dos conjuntos ativos.`}
      >
        <span tabIndex={0} className="nums inline-flex cursor-help flex-col gap-1">
          <span>
            {formatCurrency((meta.gastoHojeCentavos ?? 0) / 100, m)}{' '}
            <span className="text-muted-foreground">
              de {formatCurrency(meta.orcamentoDiarioCentavos / 100, m)}
            </span>
          </span>
          <Meter value={Math.min(1, usado)} label="orçamento usado" size="sm" />
        </span>
      </Tooltip>
      {ajustar ? <div>{ajustar}</div> : null}
    </TableCell>
  );
}

/** Dinheiro em centavos → "R$ 1,23"; `null` vira traço com o porquê. */
function Dinheiro({
  centavos,
  moeda,
  ajuda,
  forte = false,
}: {
  centavos: number | null;
  moeda: string | null;
  ajuda: string;
  forte?: boolean;
}) {
  return (
    <TableCell numeric nowrap>
      {centavos === null ? (
        <Tooltip content={ajuda}>
          <span tabIndex={0} className="cursor-help text-muted-foreground">
            {TRACO}
          </span>
        </Tooltip>
      ) : (
        <span className={forte ? 'font-semibold' : undefined}>
          {formatCurrency(centavos / 100, moeda ?? 'BRL')}
        </span>
      )}
    </TableCell>
  );
}

const VEICULACAO: Record<string, { rotulo: string; tone: 'success' | 'warning' | 'neutral' }> = {
  ACTIVE: { rotulo: 'Ativa', tone: 'success' },
  PAUSED: { rotulo: 'Pausada', tone: 'warning' },
  CAMPAIGN_PAUSED: { rotulo: 'Pausada', tone: 'warning' },
  ADSET_PAUSED: { rotulo: 'Pausada', tone: 'warning' },
  ARCHIVED: { rotulo: 'Arquivada', tone: 'neutral' },
  DELETED: { rotulo: 'Excluída', tone: 'neutral' },
};

function Veiculacao({ meta }: { meta: Meta | null }) {
  if (!meta || meta.veiculacao === null) {
    return (
      <TableCell nowrap>
        <span className="text-muted-foreground">{TRACO}</span>
      </TableCell>
    );
  }
  const v = VEICULACAO[meta.veiculacao] ?? { rotulo: meta.veiculacao, tone: 'neutral' as const };
  return (
    <TableCell nowrap>
      <Badge tone={v.tone}>{v.rotulo}</Badge>
    </TableCell>
  );
}

function LinhaFunil({
  linha,
  nivel,
  expansivel = false,
  aberto = false,
  onAlternar,
  ajudaDoNome,
  onAjustarOrcamento,
}: {
  linha: LinhaDoFunil;
  nivel: 0 | 1;
  onAjustarOrcamento?: (() => void) | undefined;
  expansivel?: boolean;
  aberto?: boolean;
  onAlternar?: () => void;
  ajudaDoNome?: string;
}) {
  const c = linha.custos;
  const semGasto =
    nivel === 0
      ? 'Sem gasto: nenhuma campanha da Meta vinculada a este bloco, ou a Meta não trouxe gasto no período. Vincule em Campanhas › ⋯ › Meta Ads.'
      : 'A Meta não trouxe gasto desta campanha no período.';
  const Seta = aberto ? ChevronDown : ChevronRight;
  return (
    <TableRow>
      <TableRowHeader>
        {/* Largura mínima: sem ela o nome do bloco quebrava em seis linhas ao lado de 12 colunas. */}
        <span
          className={
            nivel === 1 ? 'flex min-w-56 items-start gap-1 pl-6' : 'flex min-w-56 items-start gap-1'
          }
        >
          {expansivel ? (
            <button
              type="button"
              onClick={onAlternar}
              aria-expanded={aberto}
              aria-label={`${aberto ? 'Fechar' : 'Abrir'} as campanhas da Meta de ${linha.nome}`}
              className="mt-0.5 rounded-sm text-muted-foreground hover:text-foreground"
            >
              <Seta className="size-4" aria-hidden="true" />
            </button>
          ) : nivel === 0 ? (
            <span className="inline-block w-4" />
          ) : null}
          <span className="min-w-0">
            {ajudaDoNome ? (
              <Tooltip content={ajudaDoNome}>
                <span tabIndex={0} className="block cursor-help truncate text-muted-foreground">
                  {linha.nome}
                </span>
              </Tooltip>
            ) : linha.href ? (
              <Link
                to={linha.href}
                className="block truncate font-medium text-foreground underline-offset-2 hover:underline"
              >
                {linha.nome}
              </Link>
            ) : (
              <span className={nivel === 0 ? 'block truncate' : 'block truncate text-sm'}>
                {linha.nome}
              </span>
            )}
            {linha.detalhe ? (
              <span className="nums block text-2xs text-muted-foreground">{linha.detalhe}</span>
            ) : null}
          </span>
        </span>
      </TableRowHeader>
      <Veiculacao meta={linha.meta} />
      <Dinheiro centavos={c?.gastoCentavos ?? null} moeda={c?.moeda ?? null} ajuda={semGasto} />
      <Orcamento meta={linha.meta} moeda={c?.moeda ?? null} onAjustar={onAjustarOrcamento} />
      <Dinheiro
        centavos={linha.meta?.custoPorLeadCentavos ?? null}
        moeda={c?.moeda ?? null}
        ajuda="Gasto ÷ leads que o pixel informou à Meta. Sem evento de lead no pixel (ou sem gasto), não há conta."
      />
      <Dinheiro
        centavos={c?.custoPorEntradaCentavos ?? null}
        moeda={c?.moeda ?? null}
        ajuda="Gasto ÷ entradas no grupo. Sem gasto conhecido, ou sem nenhuma entrada no período, não há conta — e não é zero."
      />
      <Dinheiro
        centavos={c?.custoPorPessoaQueFicouCentavos ?? null}
        moeda={c?.moeda ?? null}
        ajuda="Gasto ÷ quem entrou e não saiu no mesmo dia."
        forte
      />
      <TableCell numeric>
        {linha.visitas === null ? (
          <Tooltip content={linha.motivoSemVisitas ?? ''}>
            <span tabIndex={0} className="cursor-help text-muted-foreground">
              {TRACO}
            </span>
          </Tooltip>
        ) : (
          formatNumber(linha.visitas)
        )}
      </TableCell>
      <TableCell numeric>{formatNumber(linha.cliques)}</TableCell>
      <Percentual
        valor={linha.taxaDeClique}
        ajuda="De quem chegou na sua página, quantos clicaram no botão do grupo. Mede a PÁGINA."
      />
      {/*
       * Invariante 9: este salto depende do casamento probabilístico, então vai com o “≥”
       * colado — é estimativa por baixo, e o sinal diz isso sem nota de rodapé.
       */}
      <Percentual
        valor={linha.taxaDeEntrada}
        prefixo="≥ "
        ajuda="De quem clicou, quantos entraram no grupo. Mede o atrito do WhatsApp. Estimativa por baixo: o salto para o WhatsApp não devolve identificador."
      />
      <TableCell numeric>
        {(linha.semHorario ?? 0) > 0 ? (
          <Tooltip
            content={`Inclui ${formatNumber(linha.semHorario ?? 0)} que entraram enquanto o WhatsApp estava desconectado — achados quando ele voltou, sem horário exato.`}
          >
            <span tabIndex={0} className="cursor-help">
              {formatNumber(linha.entradas)}
              <span className="block text-2xs text-muted-foreground">
                {formatNumber(linha.semHorario ?? 0)} sem horário
              </span>
            </span>
          </Tooltip>
        ) : (
          formatNumber(linha.entradas)
        )}
      </TableCell>
      <TableCell nowrap>
        {linha.evasao === null || linha.evasao.taxa === null ? (
          <Tooltip content="Ninguém entrou por aqui no período — sem entrada não há evasão para medir.">
            <span tabIndex={0} className="cursor-help text-muted-foreground">
              {TRACO}
            </span>
          </Tooltip>
        ) : (
          <Tooltip
            content={`${formatNumber(linha.evasao.sairamNoMesmoDia)} de ${formatNumber(
              linha.evasao.entraram,
            )} que entraram saíram por conta própria antes de o dia virar. Remoção por admin e saída sem causa conhecida não entram nesta conta.`}
          >
            <span tabIndex={0} className="nums inline-flex cursor-help flex-col gap-1">
              <span>
                {formatPercent(linha.evasao.taxa, 1)}{' '}
                <span className="text-muted-foreground">
                  · {formatNumber(linha.evasao.sairamNoMesmoDia)}
                </span>
              </span>
              {/* a barra CHEIA é ruim aqui — por isso não usa a cor de destaque */}
              <Meter value={linha.evasao.taxa} label="evasão" size="sm" tone="neutral" />
            </span>
          </Tooltip>
        )}
      </TableCell>
      <Ficaram24h r={linha.retencao24h} />
      <EvasaoDoMarco r={linha.evasaoD1} marco="1º dia" />
      <EvasaoDoMarco r={linha.evasaoD7} marco="7 dias" />
      <TableCell numeric nowrap>
        {linha.custoPorFicouD7Centavos === null ? (
          <Tooltip content="Gasto ÷ quem ficou 7 dias. Sem gasto conhecido da Meta (sem vínculo ou sincronização parada) ou sem turma que já completou 7 dias.">
            <span tabIndex={0} className="cursor-help text-muted-foreground">
              {TRACO}
            </span>
          </Tooltip>
        ) : (
          <span className="nums">
            {formatCurrency(linha.custoPorFicouD7Centavos / 100, linha.custos?.moeda ?? 'BRL')}
          </span>
        )}
      </TableCell>
    </TableRow>
  );
}

/**
 * Evasão por marco de calendário (ADR-0019): quem saiu por conta própria até o fim do dia
 * seguinte (1º dia) ou do 7º dia. Diferente de "Ficaram 24h", que é janela de relógio.
 */
function EvasaoDoMarco({ r, marco }: { r: AnalyticsFunnelRow['evasaoD1'] | null; marco: string }) {
  if (!r || r.taxa === null) {
    const pend = r?.pendentes ?? 0;
    return (
      <TableCell nowrap>
        <Tooltip
          content={
            pend > 0
              ? `Ainda não deu tempo: ${formatNumber(pend)} ${pend === 1 ? 'pessoa ainda não completou' : 'pessoas ainda não completaram'} o marco de ${marco}.`
              : 'Ninguém entrou nos grupos deste recorte no período.'
          }
        >
          <span tabIndex={0} className="cursor-help text-muted-foreground">
            {TRACO}
          </span>
        </Tooltip>
      </TableCell>
    );
  }
  return (
    <TableCell nowrap>
      <span className="nums inline-flex items-center gap-1.5">
        <Tooltip
          content={`De ${formatNumber(r.vencidos)} pessoas cujas turmas já completaram ${marco}, ${formatPercent(r.taxa, 0)} saíram por conta própria. Quem você removeu fica fora. É por calendário, não as 24h de relógio de "Ficaram 24h".`}
        >
          <span tabIndex={0} className="cursor-help">
            {formatPercent(r.taxa, 0)}
          </span>
        </Tooltip>
        <TrafficLight
          status={r.semaforo}
          compact
          hint={
            r.meta !== null
              ? `Meta: até ${formatPercent(r.meta, 0)} · Agora: ${formatPercent(r.taxa, 0)}`
              : 'Contra a mediana de 28 dias do workspace'
          }
        />
      </span>
    </TableCell>
  );
}

/**
 * Quem entrou e continuava no grupo 24h depois. Só conta quem já completou as 24h — os
 * que entraram há menos tempo aparecem como "aguardando", e não como "ficaram".
 */
function Ficaram24h({ r }: { r: AnalyticsFunnelRow['retencao24h'] | null }) {
  if (!r || r.taxa === null) {
    const pend = r?.pendentes ?? 0;
    return (
      <TableCell nowrap>
        <Tooltip
          content={
            pend > 0
              ? `${formatNumber(pend)} ${pend === 1 ? 'pessoa entrou' : 'pessoas entraram'} há menos de 24h — o número sai quando completarem.`
              : 'Ninguém entrou por aqui no período.'
          }
        >
          <span tabIndex={0} className="cursor-help text-muted-foreground">
            {pend > 0 ? 'aguardando 24h' : TRACO}
          </span>
        </Tooltip>
      </TableCell>
    );
  }
  return (
    <TableCell nowrap>
      <Tooltip
        content={`${formatNumber(r.ficaram)} de ${formatNumber(r.elegiveis)} que entraram há mais de 24h continuavam no grupo 24h depois.${r.pendentes > 0 ? ` Outras ${formatNumber(r.pendentes)} ainda estão completando as 24h.` : ''}`}
      >
        <span tabIndex={0} className="nums inline-flex cursor-help flex-col gap-1">
          <span>
            {formatPercent(r.taxa, 0)}{' '}
            <span className="text-muted-foreground">
              · {formatNumber(r.ficaram)} de {formatNumber(r.elegiveis)}
            </span>
          </span>
          <Meter value={r.taxa} label="ficaram 24h" size="sm" />
        </span>
      </Tooltip>
    </TableCell>
  );
}

/** Uma célula de porcentagem que mostra “—” com explicação quando não há denominador. */
function Percentual({
  valor,
  ajuda,
  prefixo = '',
}: {
  valor: number | null;
  ajuda: string;
  prefixo?: string;
}) {
  return (
    <TableCell nowrap>
      <Tooltip content={valor === null ? `${ajuda} Ainda não há como calcular.` : ajuda}>
        <span
          tabIndex={0}
          className={
            valor === null
              ? 'inline-flex cursor-help flex-col gap-1 text-muted-foreground'
              : 'nums inline-flex cursor-help flex-col gap-1'
          }
        >
          <span>{valor === null ? TRACO : `${prefixo}${formatPercent(valor, 1)}`}</span>
          {/*
           * O medidor lê a FORMA antes do dígito: comparando linhas, a altura das barrinhas
           * responde "qual campanha vaza mais" sem ler número. É proporção, não semáforo.
           */}
          <Meter value={valor} label="proporção" size="sm" />
        </span>
      </Tooltip>
    </TableCell>
  );
}

/**
 * "De quando é este gasto?" — dito ao lado do número (pedido do dono, 06/10).
 *
 * O gasto chega da Meta pelo sync automático de 10 em 10 min; a tela se atualiza sozinha a
 * cada minuto. O selo mostra a leitura MAIS ANTIGA entre as contas ativas: se uma ficou
 * para trás, é ela que decide o quanto o total pode estar velho.
 */
export function MetaFreshness() {
  const { workspace, role } = useWorkspace();
  const pode = can(role, 'integration:read');
  const conexoes = useAdsConnections(workspace.id, pode);
  if (!pode || !conexoes.data) return null;
  const ativas = conexoes.data.filter((c) => c.status !== 'DISABLED');
  if (ativas.length === 0) return null;
  const leituras = ativas.map((c) => c.lastSyncAt);
  const algumaNunca = leituras.some((l) => l === null);
  const maisVelha = leituras.filter((l): l is string => l !== null).sort()[0];
  const minutos =
    maisVelha === undefined ? null : (Date.now() - new Date(maisVelha).getTime()) / 60_000;
  const atrasada = algumaNunca || minutos === null || minutos > 30;
  const texto = algumaNunca
    ? 'Meta: aguardando a primeira leitura'
    : `Meta atualizada ${formatRelative(maisVelha ?? null) ?? ''}`;
  return (
    <Tooltip content="O gasto da Meta é lido sozinho a cada 10 minutos (a própria Meta recalcula o gasto do dia a cada 15–20 min). A tela se atualiza sozinha a cada minuto.">
      <span
        tabIndex={0}
        className="inline-flex cursor-help items-center gap-1.5 text-xs text-muted-foreground"
      >
        <span
          aria-hidden="true"
          className={cn('size-1.5 rounded-pill', atrasada ? 'bg-warning' : 'bg-success')}
        />
        {texto}
      </span>
    </Tooltip>
  );
}
